'use client';

import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AccountDetail, BrokerFields, farsiLabels } from '@/types/brokers/slug';
import { CloseCircle, ExportSquare, TickCircle } from 'iconsax-react';
import Link from 'next/link';

interface BrokerAccountTabsProps {
    BrokerAccounts?: BrokerFields['account'];
    title?: string;
    ctaButton?: {
        url?: string;
        title?: string;
    };
    enTitle?: string | undefined;
}

const BrokerAccountTabs: React.FC<BrokerAccountTabsProps> = ({
    BrokerAccounts,
    title,
    ctaButton,
    enTitle,
}) => {
    const accounts = Array.isArray(BrokerAccounts)
        ? BrokerAccounts
        : BrokerAccounts
          ? [BrokerAccounts]
          : [];

    if (accounts.length === 0) {
        return null;
    }

    type Item = {
        key: keyof AccountDetail;
        value: any;
        formatter?: (value: any) => string;
        after?: React.ReactNode;
    };

    const allItems: { [key in keyof AccountDetail]: Item } = {
        title: {
            key: 'title',
            value: '',
        },
        typeOrders: {
            key: 'typeOrders',
            value: '',
        },
        requote: {
            key: 'requote',
            value: '',
        },
        minDeposite: {
            key: 'minDeposite',
            value: '',
            formatter: (value: number) => (value !== null ? `${value} دلار  ` : ''),
        },
        stopOut: {
            key: 'stopOut',
            value: '',
            formatter: (value: number) => (value !== null ? `${value} درصد ` : ''),
        },
        islamic: {
            key: 'islamic',
            value: '',
        },
        leverage: {
            key: 'leverage',
            value: '',
            formatter: (value: number) => `1:${value}`,
        },
        levelMarginCall: {
            key: 'levelMarginCall',
            value: '',
            formatter: (value: number) => (value !== null ? `${value} درصد ` : ''),
        },
        withoutCommission: {
            key: 'withoutCommission',
            value: '',
        },
        spread: {
            key: 'spread',
            value: '',
            formatter: (value: number) => (value !== null ? `از ${value} پیپ` : ''),
        },
        stopLevel: {
            key: 'stopLevel',
            value: '',
            formatter: (value: string) => (value !== null ? value : ''),
        },
        typeManageSystemSpread: {
            key: 'typeManageSystemSpread',
            value: '',
        },
        commission: {
            key: 'commission',
            value: '',
            formatter: (value: number) => (value !== null ? `${value} دلار (دوطرف)` : ''),
        },
        maxOpenOrders: {
            key: 'maxOpenOrders',
            value: '',
            formatter: (value: string) => (value !== null ? value : ''),
        },
        manageSystemOrders: {
            key: 'manageSystemOrders',
            value: '',
        },
        platform: {
            key: 'platform',
            value: '',
            formatter: (value: string[] | null | undefined) => {
                if (!Array.isArray(value)) return '-';
                const platformMap: { [key: string]: string } = {
                    mt4: 'MT4',
                    mt5: 'MT5',
                    ctrader: 'CTrader',
                    exclusive: 'پلتفرم اختصاصی',
                };
                return value.map((v) => platformMap[v] || v).join(', ');
            },
        },
        activity: {
            key: 'activity',
            value: '',
        },
    };

    return (
        <div className={'py-2 border-b dark:border-white/30 mb-3 lg:px-3'}>
            <div className={'flex flex-wrap justify-between items-center max-lg:mb-4'}>
                <h3 className='text-xl mb-4 lg:mb-1'> {'مقایسه حساب‌های' + ' ' + title}</h3>
                {ctaButton && ctaButton.url && ctaButton.title && (
                    <Link
                        className={
                            'bg-bg dark:bg-progress/80 hover:bg-progress group hover:text-white transition-colors max-lg:basis-full justify-center max-lg:order-last flex text-nowrap items-center gap-2 max-lg:py-3 p-2 lg:px-3 rounded-2xl max-lg:mt-4'
                        }
                        href={ctaButton.url}
                        rel={'nofollow'}
                        target={'_blank'}
                        id={`${enTitle + '-cta'}`}
                    >
                        <ExportSquare
                            size={16}
                            className={
                                'stroke-primary/60 group-hover:stroke-white/80 transition-colors dark:stroke-white/80'
                            }
                        />
                        {ctaButton.title}
                    </Link>
                )}
                <Tabs
                    defaultValue={accounts[0]?.title || 'Account'}
                    className='w-full basis-full'
                    dir={'rtl'}
                >
                    <TabsList
                        className={
                            'bg-transparent w-full justify-start border-b px-0 dark:border-white/30 gap-3 overflow-x-auto h-auto rounded-none shadow-none pb-0'
                        }
                    >
                        {accounts.map((account, index) => (
                            <TabsTrigger
                                className={
                                    'border-b-2 border-b-transparent TabsButton px-2 bg-transparent shadow-transparent font-yekanBakh dark:data-[state=active]:text-blue-400 dark:data-[state=active]:border-blue-400'
                                }
                                key={index}
                                value={account?.title || ''}
                            >
                                {account?.title || `Account ${index + 1}`}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                    {accounts.map((account, index) => {
                        const mainItems: Item[] = Object.keys(allItems).map((key) => {
                            const fieldKey = key as keyof AccountDetail;
                            const item = allItems[fieldKey];
                            let displayValue = account.detail[fieldKey];
                            if (item.formatter) {
                                displayValue = item.formatter(displayValue);
                            }
                            return {
                                ...item,
                                value: displayValue,
                            };
                        });

                        return (
                            <TabsContent key={index} value={account?.title || ''}>
                                <ul className='lg:grid-cols-3 sm:grid-cols-2 grid-cols-1 grid gap-x-4 items-start'>
                                    {mainItems.map(({ key, value, after }, itemIndex) => {
                                        // Calculate the number of items per row at `lg` breakpoint (3 items per row).
                                        const isLastRow =
                                            itemIndex >=
                                            mainItems.length - (mainItems.length % 3 || 3);

                                        return (
                                            <li
                                                key={key}
                                                className={`flex flex-row items-center sm:items-center space-y-1 sm:space-y-0 sm:space-x-2 justify-between w-full py-1.5 border-b border-gray-100  dark:border-white/20 ${isLastRow ? 'lg:border-b-0' : ''} max-lg:border-b`}
                                            >
                                                <span className='font-light text-xs'>
                                                    {farsiLabels[key] || key}:
                                                </span>

                                                <div className='inline-flex gap-1 items-center'>
                                                    <span className='text-sm font-semibold text-left font-yekanBakh capitalize'>
                                                        {value}
                                                    </span>
                                                    {value !== '-' &&
                                                        (account.detail[key] === true ? (
                                                            <TickCircle
                                                                size={16}
                                                                className='stroke-white fill-green-500'
                                                            />
                                                        ) : account.detail[key] === null &&
                                                          account.detail[key] !== '-' ? (
                                                            <CloseCircle
                                                                size={16}
                                                                className='stroke-white fill-red-500'
                                                            />
                                                        ) : null)}
                                                </div>
                                                {after && (
                                                    <div className='mt-1 sm:mt-0'>{after}</div>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </TabsContent>
                        );
                    })}
                </Tabs>
            </div>
        </div>
    );
};

export default BrokerAccountTabs;
